import { useEffect } from "react"
import type { ChatSession } from "@stockline/shared/types/ai/chat-session"
import { CHAT_SESSION_TABLE } from "../../../lib/offline/resources/ai/chat-session/db"
import { useAISync } from "../providers/AISyncProvider"
import { useActiveChatSession } from "../stores/activeChatSessionStore"
import { useChatMutations } from "./useChatMutations"

/**
 * Reconcile the DB to match the flicker-fix display rule: when the user is
 * actively looking at a focused chat whose row is still `unread`, clear it.
 *
 * The list + badge already TREAT the active+focused session as read at display
 * time (so a reply never flashes a dot on the chat in front of you). This hook
 * is the durable counterpart — it pushes `unread = false` so the read state
 * persists across reloads and other devices. It reacts to:
 *   - the active session changing (user opens a different chat),
 *   - focus returning to the tab,
 *   - a fresh reply flipping the active session's row back to `unread` while
 *     the user is already looking at it (reactive via useChatSession).
 *
 * Mount it once, high up (the AI page / sync provider). It is intentionally a
 * no-op when there is no active+focused unread session.
 */
export function useClearUnreadOnFocus(): void {
  const { activeSessionId, isFocused } = useActiveChatSession()
  const { reactive } = useAISync()
  const { markSessionRead } = useChatMutations()

  // Reactive read of the active session so a reply landing on it (flipping
  // `unread` true) re-triggers the clear even without a focus/active change.
  const active = reactive.useOne<ChatSession>(
    CHAT_SESSION_TABLE,
    activeSessionId ?? ""
  )

  useEffect(() => {
    if (!activeSessionId || !isFocused) return
    if (!active || active.deletedAt || !active.unread) return
    void markSessionRead(activeSessionId)
  }, [activeSessionId, isFocused, active, markSessionRead])
}
